import React, { memo, useEffect, useState } from 'react';
import { Box, IconButton, Tab, Tabs, Tooltip, Typography, useMediaQuery } from '@mui/material';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import '@fontsource/rubik';
import { KANBAN_STAGES } from '../../../constants/constants';
import { useKanbanBoardSummary } from '../hooks/useKanbanBoardSummary';
import { useQuickTurnLookup } from '../hooks/useQuickTurnLookup';
import KanbanColumn from './KanbanColumn';

// Sentinel tab value — not a real stage key — meaning "show every column stacked".
const ALL_STAGES_KEY = '__all__';

const KanbanBoard = ({ orgId, onCardClick, name, dueWithinHours, editorId, eventTypeId }) => {
  const { summaryByKey, isLoading, isError, refetch, isFetching } =
    useKanbanBoardSummary({ orgId, name, editorId, eventTypeId, dueWithinHours });
  const quickTurnLookup = useQuickTurnLookup(orgId);

  // Below 600px, columns stack full-width and are switched via tabs instead of all
  // being shown side by side — everything else (data fetching, search, filters) is
  // unchanged; this only controls which column(s) are visible.
  const isMobileView = useMediaQuery('(max-width:599px)');
  const [selectedStageKey, setSelectedStageKey] = useState(ALL_STAGES_KEY);
  const showAllStages = selectedStageKey === ALL_STAGES_KEY;
  const totalAcrossStages = KANBAN_STAGES.reduce((sum, s) => sum + (summaryByKey[s.key]?.total ?? 0), 0);

  // If the selected stage becomes invalid (shouldn't normally happen — KANBAN_STAGES
  // is static) fall back to "All" rather than showing a blank board.
  useEffect(() => {
    if (isMobileView && !showAllStages && !KANBAN_STAGES.some((s) => s.key === selectedStageKey)) {
      setSelectedStageKey(ALL_STAGES_KEY);
    }
  }, [isMobileView, showAllStages, selectedStageKey]);

  return (
    <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Toolbar */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          mb: { xs: 1, lg: 1.5 },
          gap: 1,
        }}
      >
        {isError && (
          <Typography
            sx={{ fontSize: '12px', fontFamily: 'Rubik', color: 'error.main', mr: 1 }}
          >
            Failed to load projects.
          </Typography>
        )}
        <Tooltip title="Refresh board">
          <span>
            <IconButton
              size="small"
              onClick={() => refetch()}
              disabled={isFetching}
              aria-label="Refresh board"
              sx={{
                transition: 'transform 0.4s',
                ...(isFetching ? { animation: 'spin 1s linear infinite' } : {}),
                '@keyframes spin': { '100%': { transform: 'rotate(360deg)' } },
              }}
            >
              <RefreshRoundedIcon sx={{ fontSize: { xs: '18px', lg: '20px' } }} />
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      {/* Mobile-only stage tabs — horizontally scrollable; picks which single column is visible below */}
      {isMobileView && (
        <Tabs
          value={selectedStageKey}
          onChange={(_, value) => setSelectedStageKey(value)}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          aria-label="Kanban stage tabs"
          sx={{
            minHeight: '34px',
            mb: 1,
            flexShrink: 0,
            borderBottom: '1px solid #E8EAF0',
            '& .MuiTabs-indicator': { backgroundColor: '#00D744', height: '3px', borderRadius: '2px' },
          }}
        >
          <Tab
            value={ALL_STAGES_KEY}
            label={`All (${isLoading ? '–' : totalAcrossStages})`}
            sx={{
              textTransform: 'none',
              fontFamily: 'Rubik',
              fontWeight: 600,
              fontSize: '11.5px',
              minHeight: '34px',
              minWidth: 'auto',
              px: 1.5,
              color: '#5A6474',
              '&.Mui-selected': { color: '#08231B' },
            }}
          />
          {KANBAN_STAGES.map((stage) => (
            <Tab
              key={stage.id}
              value={stage.key}
              label={`${stage.label} (${isLoading ? '–' : summaryByKey[stage.key]?.total ?? 0})`}
              sx={{
                textTransform: 'none',
                fontFamily: 'Rubik',
                fontWeight: 600,
                fontSize: '11.5px',
                minHeight: '34px',
                minWidth: 'auto',
                px: 1.5,
                color: '#5A6474',
                '&.Mui-selected': { color: '#08231B' },
              }}
            />
          ))}
        </Tabs>
      )}

      {/* Board — horizontally scrollable on desktop/tablet; on mobile only the tab-selected column is visible */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          gap: { xs: 2, sm: 1.5, md: 1.75 },
          overflowX: { xs: 'visible', sm: 'auto' },
          overflowY: { xs: 'visible', sm: 'visible' },
          pb: { sm: 2 },
          scrollbarWidth: 'thin',
          scrollbarColor: 'rgba(0,0,0,0.12) transparent',
          '&::-webkit-scrollbar': { height: '5px' },
          '&::-webkit-scrollbar-track': { background: 'transparent' },
          '&::-webkit-scrollbar-thumb': {
            background: 'rgba(0,0,0,0.12)',
            borderRadius: '8px',
          },
          alignItems: { xs: 'stretch', sm: 'stretch' },
        }}
      >
        {KANBAN_STAGES.map((stage) => (
          <KanbanColumn
            key={stage.id}
            stageId={stage.id}
            stageKey={stage.key}
            stageLabel={stage.label}
            stageColor={stage.color}
            summary={summaryByKey[stage.key]}
            quickTurnLookup={quickTurnLookup}
            orgId={orgId}
            name={name}
            isLoading={isLoading}
            onCardClick={onCardClick}
            dueWithinHours={dueWithinHours}
            editorId={editorId}
            eventTypeId={eventTypeId}
            hidden={isMobileView && !showAllStages && stage.key !== selectedStageKey}
          />
        ))}
      </Box>
    </Box>
  );
};

export default memo(KanbanBoard);
